import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
// Self-hosted Inter font weights — previously loaded from
// fonts.googleapis.com via CSS @import, which failed with
// ERR_CONNECTION_RESET on cellular-gateway kiosks (Indian carriers
// inject TCP RSTs on US-origin font traffic). @fontsource bundles the
// woff2 files into the app build, so the font is served from the same
// origin as the rest of the app and never leaves India-hosted infra.
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import './index.css';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import { initAnalytics } from './analytics';

// Sentry — gated on REACT_APP_SENTRY_DSN being set at build time.
// Empty DSN is a full no-op: Sentry.init() is skipped entirely so
// dev/localhost builds don't ping the Sentry backend. Wrap the app
// in Sentry's ErrorBoundary unconditionally (it's a no-op with no
// client, and when the client is active it catches React render
// errors that uncaught-exception handlers miss).
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_ENV_MODE || 'development',
    release: `feedback-app@${process.env.REACT_APP_VERSION || 'dev'}`,
    tracesSampleRate: 0.1,
    // Kiosk mode auto-disables analytics (analytics.js detects the
    // AndroidDevice bridge + the " wv)" substring in the UA). Keep
    // Sentry tracing on regardless so we see crashes from the bundle
    // running inside the WebView — that's the whole point.
    beforeBreadcrumb(breadcrumb) {
      // Drop chatty console breadcrumbs in production — our app
      // logs a lot to console.log and it'd dominate Sentry payloads.
      if (breadcrumb.category === 'console' && breadcrumb.level !== 'error') {
        return null;
      }
      return breadcrumb;
    },
  });
}

// Initialize PostHog analytics
initAnalytics();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
